import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Notify} from 'quasar';
import {IBaseResponse, WebService} from '../../services';
import {mergeAndAdd} from '..';
import {IRecentItem} from './interfaces/IRecentItem';

export interface IRecentItemStoreState {
	recentItems: IRecentItem[];
}

const WAIT_DELETE_UNDO_MS = 5000;

/**
 * sort in descending order of creation
 *
 * If items have the same creation stamp, they were created in
 * the same order/ticket. Sort those by name, ascending.
 *
 * @param a
 * @param b
 */
const sortRecentItems = (a: IRecentItem, b: IRecentItem) => {
	if(a.created === b.created) {
		return a.name.localeCompare(b.name);
	}

	// if b - a is negative, sort b earlier
	return b.created - a.created;
};

@Module({name: 'recentItems', namespaced: true})
class RecentItemModule extends VuexModule implements IRecentItemStoreState {
	recentItems: IRecentItem[] = [];

	get itemByID() {
		return (id: IRecentItem['id']) => this.recentItems.find(ri => ri.id === id);
	}

	get itemName() {
		return (id: IRecentItem['id']) => (this.itemByID(id)?.name ?? '').trim();
	}

	@Action
	public async fetchRecentItems() {
		const response = await WebService.authenticatedClient.get<IBaseResponse<{recentItems: IRecentItem[]}>>('recent_items');
		this.setRecentItems(response.data.data.recentItems);
	}

	@Action
	public async deleteRecentItem(recentItemId: string) {
		return new Promise<boolean>((resolve, reject) => {
			// immediately remove item locally
			const recentItem = this.recentItems.find(recItem => recItem.id === recentItemId);
			if(recentItem) {
				this.removeRecentItem(recentItemId);

				/*
				 * Tell the user it's removed and give him an "Undo".
				 * We're delaying the actual removal until the undo
				 * timeout expires.
				 *
				 * No event emits when the Quasar Notify closes. Instead,
				 * use an explicit timeout with Notify. Wait that same
				 * amount of time (with a little extra for animations and
				 * such) before attempting an actual delete.
				 *
				 * The Notify remains in place throughout the SPA. Because
				 * this store isn't tied to any particular
				 * component/page/route, the timeout also remains active
				 * even if the user navigates away.
				 */
				const timeoutID = setTimeout(async () => {
					try {
						const response = await WebService.authenticatedClient.post<IBaseResponse<{ success: boolean }>>(
							`recent_items/${recentItemId}/delete`
						);
						resolve(response.data.data.success);
					} catch(e) {
						// refresh local (wait so that caller can access restored item)
						await this.fetchRecentItems();
						reject(e);
					}
				}, WAIT_DELETE_UNDO_MS + 750);


				Notify.create({
					message: `"${recentItem.name.trim()}" was removed.`,
					color: 'dark',
					progress: true,
					textColor: 'white',
					group: false,
					timeout: WAIT_DELETE_UNDO_MS,
					actions: [
						{
							label: 'Undo',
							color: 'white',
							handler: () => {
								clearTimeout(timeoutID);
								this.addRecentItem(recentItem);
								resolve(false);
							}
						}
					]
				});
			} else {
				// no match to delete
				resolve(true);
			}
		});
	}

	@Mutation
	public setRecentItems(recentItems: IRecentItem[]) {
		this.recentItems = recentItems.sort(sortRecentItems);
	}

	@Mutation
	public addRecentItem(recentItem: IRecentItem) {
		mergeAndAdd(
			this.recentItems,
			[recentItem],
			['id'],
			sortRecentItems
		);
	}

	@Mutation
	public removeRecentItem(id: string) {
		this.recentItems = this.recentItems.filter(recentItem => {
			return recentItem.id !== id;
		});
	}

	@Mutation
	public clearRecentItems() {
		this.recentItems = [];
	}
}


export {
	RecentItemModule
};
